import React from "react";
import { renderToString } from "react-dom/server";
import { getMainAxisSelectedPoints } from "@ui/Highcharts/plugins/stickyTooltip";
import { FLAG_AXIS } from "../../Chart/series/getAnalysisFlagSerie";
import validateInterface from "@ui/lib/validateInterface";
import type { Status } from "../../Chart/PointMetadata";
import { FailureMetadata, PointMetadata, QUANTITY_FLAG_METADATA } from "../../Chart/PointMetadata";
import type { Options } from "@ui/Highcharts";
import useYMinMax from "@ui/Highcharts/hooks/useYMinMax";
import useAnalysisXAxis from "../../Chart/hooks/useAnalysisXAxis";
import formatNumber from "@ui/i18n/formatNumber";
import t from "../../../../i18n";
import type { Project } from "../../../../api/useProjects";
import type { ActiveAnalysis } from "../../../../api/useActiveAnalysesStream";
import type { Source } from "../../../../api/useSources";
import useTooltipClick from "@ui/Highcharts/hooks/useTooltipClick";
import useNoDataPlotBands from "@ui/Highcharts/hooks/useNoDataPlotBands";
import { useDiagnosticWithQuantityNoDataIntervals } from "../../useNoDataIntervals";
import { useFailureValuesMutation, type FailureValuesMutation } from "../useFailureStatusMutation";
import { useTranslations } from "../../../../api/useTranslations";
import type { FailureValueStatus } from "../../../../api/useFailureMetadata";
import { useSettings } from "../../../../api/useSettings";
import useGroupBreaks from "../../Chart/hooks/useGroupBreaks";
import { GLOBAL_DIAGNOSIS_CHART } from "./hooks/useDiagnosisChartType";
import type { Reparability } from "../../../../api/Reparability";
import { reparabilities } from "../../../../api/Reparability";
import { ChartTooltip } from "../../Chart/ChartTooltip";
import { FaultDescriptionChartTooltip } from "../../Chart/ChartTooltip/diagnosis/FaultDescriptionChartTooltip";
import { AnalysisDateChartTooltip } from "../../Chart/ChartTooltip/common/AnalysisDateChartTooltip";
import { FaultInfoChartTooltip } from "../../Chart/ChartTooltip/diagnosis/FaultInfoChartTooltip";
import { FaultStatusChartTooltip } from "../../Chart/ChartTooltip/diagnosis/FaultStatusChartTooltip";
import { FaultReparabilityChartTooltip } from "../../Chart/ChartTooltip/diagnosis/FaultReparabilityChartTooltip";
import { FlagChartTooltip } from "../../Chart/ChartTooltip/common/FlagChartTooltip";
import { FaultMultiSelectChartTooltip } from "../../Chart/ChartTooltip/diagnosis/FaultMultiSelectChartTooltip";
import { EventChartTooltip } from "../../Chart/ChartTooltip/diagnosis/EventChartTooltip";
import type { IndexedFailureValues } from "../../../../api/useIndexedFailureValues";
import type { Quantity } from "../../../../api/useQuantities";
import { isFaultPromoted } from "../../../../domain/isFaultPromoted";
import { CHART_SELECTION_PLOT_LINE_MARGIN } from "../../Chart/hooks/useAnalysisSelectionPlotLines";
import type { Event } from "../../../../api/useEvents";
import { getEventPriorityText } from "../../Events/utils/event";
import { getEventStatusText } from "../../Events/utils/faultEventStatus";
import { getEventPriority } from "../../../../domain/event";

export interface FailureEvolutionHookOptions {
  source: Source;
  project: Project;
  yAxisId: string;
  chartRef: React.RefObject<Highcharts.Chart>;
  analyses: ActiveAnalysis[];
  indexedFailureValues: IndexedFailureValues;
  series: (
    | Highcharts.SeriesLineOptions
    | Highcharts.SeriesArearangeOptions
    | Highcharts.SeriesAreaOptions
    | Highcharts.SeriesScatterOptions
  )[];
  chartTypeOption: { label: string; value: string };
  qoi: Quantity | null;
  getEventForPoint?: (
    failureName: string | null,
    pointDate: number,
    isManual: boolean,
  ) => Event | null;
  getEventLink?: (event: Event) => string;
}

/**
 * Perform a rapid runtime type check & return formated payload data.
 * Catch edge case of selected point without failureId
 * (user interaction side effect).
 *
 * @param points Selected Highchart Point (with FailureMetadata)
 * @returns
 */
export function getUpdatableFailures(points: Highcharts.Point[]): {
  date: number;
  failureName: string;
}[] {
  const seen = new Set<string>();

  return points.reduce(
    (acc, point) => {
      const { analysisDate, failureName } = validateInterface(FailureMetadata, point.custom);

      if (typeof failureName === "string") {
        const naturalKey = `${analysisDate}-${failureName}`;
        if (seen.has(naturalKey)) return acc;

        seen.add(naturalKey);
        acc.push({
          date: analysisDate,
          failureName,
        });
      }

      return acc;
    },
    [] as {
      date: number;
      failureName: string;
    }[],
  );
}

/**
 * Reparability
 */
const isActionOnStatus = (status: Status): status is FailureValueStatus =>
  [
    "CONFIRMED",
    "REJECTED",
    "SUSPECTED",
    "TO_BE_ANALYSED",
    "LEGACY_REJECTED",
    "BACK_TO_BE_ANALYSED",
  ].includes(status);
const isActionOnReparability = (status: Status): status is Reparability =>
  reparabilities.includes(status as Reparability);

export const failureEvolutionTooltipClickHandler = async (
  action: Status,
  points: Highcharts.Point[],
  mutateFailureValues: FailureValuesMutation,
) => {
  // See #getFailureStatusUpdatePayload
  // Fail silently and don't perform request.
  const updatableFailures = getUpdatableFailures(points);
  if (updatableFailures.length === 0) return;

  const propertyToUpdate: {
    status?: FailureValueStatus;
    reparability?: Reparability | null;
  } = {};

  if (isActionOnReparability(action)) {
    propertyToUpdate.reparability = action;
  }
  if (isActionOnStatus(action)) {
    propertyToUpdate.status = action;
    if (
      [
        "REJECTED",
        "LEGACY_REJECTED",
        "SUSPECTED",
        "TO_BE_ANALYSED",
        "BACK_TO_BE_ANALYSED",
      ].includes(action)
    )
      propertyToUpdate.reparability = null;
  }

  await mutateFailureValues(updatableFailures, propertyToUpdate);
};

export function failureEvolutionConstantTooltipFormatter(
  contextObject: Highcharts.TooltipFormatterContextObject,
  qoiRef: React.MutableRefObject<Quantity | null>,
  project: Project,
  settings: ReturnType<typeof useSettings>,
  translations: ReturnType<typeof useTranslations>,
  getEventForPoint?: FailureEvolutionHookOptions["getEventForPoint"],
  getEventLink?: FailureEvolutionHookOptions["getEventLink"],
) {
  if (!contextObject.point.options.custom) return false;
  const metadata = validateInterface(PointMetadata, contextObject.point.options.custom);

  // no tooltip for "link" points
  if (
    (metadata.type === "FAILURE_METADATA" || metadata.type === "MANUAL_FAILURE_METADATA") &&
    !metadata.failureName
  )
    return false;

  const points = getMainAxisSelectedPoints(contextObject.series.chart);

  const humanValueParams = "human" in metadata ? metadata.human : null;
  const humanValue =
    humanValueParams &&
    formatNumber(humanValueParams.value, {
      sign: humanValueParams.sign ?? undefined,
      precision: humanValueParams.precision ?? undefined,
      unit: humanValueParams.unit ?? undefined,
    });

  const serieStatus = points.reduce((acc, point) => {
    const { failureStatus } = validateInterface(FailureMetadata, point.options.custom);
    if (!failureStatus) return acc;
    const status = [...acc, failureStatus];
    return status;
  }, [] as FailureValueStatus[]);

  const serieReparabilties = points.reduce((acc, point) => {
    const { reparability } = validateInterface(FailureMetadata, point.options.custom);
    if (!reparability || acc.includes(reparability)) return acc;
    return [...acc, reparability];
  }, [] as Reparability[]);

  const reparability = "reparability" in metadata ? metadata.reparability : null;
  const singleSelectedPointReparability =
    [...new Set(points.map(point => "reparability" in point.custom && point.custom.reparability))]
      .length === 1;
  const showReparabilityBadge = reparability
    ? points.length <= 1 ||
      (points.length > 1 && singleSelectedPointReparability) ||
      (points.length > 1 &&
        !singleSelectedPointReparability &&
        !points.map(p => p.id).includes(contextObject.point.id))
    : false;

  const reparabilityActions = reparabilities.map(reparability => ({
    text: t("reparability", reparability, translations),
    action: reparability,
    intent: "confirm",
    show:
      !singleSelectedPointReparability ||
      (serieStatus.includes("CONFIRMED") && !serieReparabilties.includes(reparability)),
  }));
  const showReparabilityAction =
    reparabilityActions.some(a => a.show) &&
    reparabilityActions.length &&
    contextObject.point.selected &&
    points.length > 0 &&
    humanValue !== null
      ? reparabilityActions
      : [];

  const status = metadata.type === "FAILURE_METADATA" ? (metadata.failureStatus ?? null) : null;
  const amplitude =
    metadata.type === "FAILURE_METADATA" && metadata.failureAmplitude
      ? (formatNumber(metadata.failureAmplitude, {
          unit: metadata.failureUnit ?? "",
          precision: 2,
        }) ?? null)
      : null;
  const lossValue = humanValue;

  const failureDisplayName = "failureDisplayName" in metadata ? metadata.failureDisplayName : null;
  const description = "failureDescription" in metadata ? metadata.failureDescription : null;
  const selectedCount = contextObject.point.selected ? points.length : 0;
  const isConfirmedFailure =
    ("failureStatus" in metadata && metadata.failureStatus === "CONFIRMED") ||
    ("isManual" in metadata && metadata.isManual);
  const isManualFailure = metadata.type === "MANUAL_FAILURE_METADATA";
  const failureName = "failureName" in metadata ? metadata.failureName : null;
  const pointDate = contextObject.point.x;
  const event = getEventForPoint ? getEventForPoint(failureName, pointDate, isManualFailure) : null;
  const eventLink = event && getEventLink ? getEventLink(event) : null;
  const eventStatus = event ? getEventStatusText(event.metadata.status) : null;
  const eventPriority = event ? getEventPriorityText(getEventPriority(event)) : null;

  const flags = metadata.type === QUANTITY_FLAG_METADATA ? metadata.flags : undefined;
  const faultType = "isManual" in metadata && metadata.isManual ? "MANUAL_FAILURE" : "FAILURE";

  const confidence =
    "confidenceIndicator" in metadata && faultType !== "MANUAL_FAILURE"
      ? (metadata.confidenceIndicator ?? null)
      : undefined;

  const quantityValues = "quantityValues" in metadata ? (metadata.quantityValues ?? {}) : {};
  const filters = "filters" in metadata ? (metadata.filters ?? []) : [];
  const isPromoted = isFaultPromoted(quantityValues, filters);

  return renderToString(
    <ChartTooltip>
      {selectedCount <= 1 ? (
        <>
          <AnalysisDateChartTooltip
            date={contextObject.point.x}
            group={metadata.group}
            label={metadata.label}
            timezone={project.timezone}
          />
          {contextObject.point.description ? (
            <FaultDescriptionChartTooltip description={contextObject.point.description} />
          ) : null}
          {contextObject.series.name ? (
            <FaultInfoChartTooltip
              type={faultType}
              failureDisplayName={failureDisplayName ?? contextObject.series.name}
              description={description}
              isPromoted={isPromoted}
              confidence={confidence}
              amplitude={amplitude}
              losses={
                qoiRef.current === null || lossValue === null
                  ? null
                  : { value: lossValue, qoi: qoiRef.current.name }
              }
            />
          ) : null}
        </>
      ) : (
        <FaultMultiSelectChartTooltip pointsSelected={points} />
      )}
      {status ? (
        <FaultStatusChartTooltip
          statusHover={status}
          statusMultipleSelected={serieStatus}
          selectedCount={selectedCount}
          automaticDiagnosisRelaunch={settings.AUTOMATIC_DIAGNOSIS_RELAUNCH}
        />
      ) : null}
      {isConfirmedFailure &&
      serieStatus.every(e => e === "CONFIRMED") &&
      (reparability !== null || selectedCount > 0) ? (
        <FaultReparabilityChartTooltip
          showBadge={showReparabilityBadge}
          reparability={reparability}
          reparabilityActions={showReparabilityAction}
          translations={translations}
        />
      ) : null}
      {selectedCount <= 1 && event && eventLink && eventStatus && eventPriority ? (
        <EventChartTooltip
          eventName={event.name}
          status={eventStatus}
          priority={eventPriority}
          link={contextObject.point.selected ? eventLink : null}
        />
      ) : null}
      {flags ? <FlagChartTooltip flags={flags} /> : null}
    </ChartTooltip>,
  );
}

const useOptions = ({
  source,
  project,
  yAxisId,
  chartRef,
  analyses,
  indexedFailureValues,
  series,
  chartTypeOption,
  qoi,
  getEventForPoint,
  getEventLink,
}: FailureEvolutionHookOptions) => {
  const settings = useSettings();
  const translations = useTranslations();
  const xBreaks = useGroupBreaks(project.id, analyses);
  const initialXAxisOptions = useAnalysisXAxis({
    chartRef,
    project,
    source,
    analyses,
    boundaries: "active-analyses",
  });

  const initialYAxisOptions = useYMinMax({
    chartRef,
    yAxisId,
    scope: `${qoi ? `sign-${-1}` : "qoi"}`,
  });

  useNoDataPlotBands({
    chartRef,
    intervals: useDiagnosticWithQuantityNoDataIntervals(analyses, indexedFailureValues),
  });

  const chartTypeOptionLabelRef = React.useRef(chartTypeOption.label);
  const qoiRef = React.useRef(qoi);
  const mutateFailureValues = useFailureValuesMutation(project);

  chartTypeOptionLabelRef.current =
    chartTypeOption.value === GLOBAL_DIAGNOSIS_CHART
      ? t("diagnosis.overview.amplitude")
      : chartTypeOption.label;

  qoiRef.current = qoi;

  useTooltipClick(
    chartRef,
    React.useCallback(
      (action: Status, points: Highcharts.Point[]) =>
        failureEvolutionTooltipClickHandler(action, points, mutateFailureValues),
      [mutateFailureValues],
    ),
  );

  const constantTooltipFormatter = React.useCallback(
    function fn(this: Highcharts.TooltipFormatterContextObject) {
      return failureEvolutionConstantTooltipFormatter(
        this,
        qoiRef,
        project,
        settings,
        translations,
        getEventForPoint,
        getEventLink,
      );
    },
    [translations, project, settings, getEventForPoint, getEventLink],
  );

  return React.useMemo(() => {
    const _: Options = {
      initial: {
        xAxis: initialXAxisOptions,
        yAxis: [initialYAxisOptions],
      },
      chart: {
        zoomType: "xy",
        marginTop: CHART_SELECTION_PLOT_LINE_MARGIN,
      },
      series,
      xAxis: {
        breaks: xBreaks,
      },
      yAxis: [
        {
          id: yAxisId,
          visible: true,
          title: {
            text: qoi
              ? t("diagnosis.chart.evolution.axis.y", qoi.name, qoi.unit)
              : t("diagnosis.overview.header.option.global"),
          },
          labels: {
            sign: false,
            enabled: true,
            unit: undefined,
          },
        },
        FLAG_AXIS,
      ],
      tooltip: {
        sticky: true,
        formatter: constantTooltipFormatter,
      },
      navigator: {
        enabled: true,
        xAxis: {
          breaks: xBreaks,
          max: Date.now(),
        },
      },
    };
    return _;
  }, [
    initialXAxisOptions,
    initialYAxisOptions,
    series,
    xBreaks,
    yAxisId,
    qoi,
    constantTooltipFormatter,
  ]);
};

export default useOptions;
